import { z } from 'zod';
import { eventSchema } from './envelope';

export const INTERACTION_COMMENT_CREATED_V1 = 'interaction.comment.created.v1' as const;
export const INTERACTION_COMMENT_REPORTED_V1 = 'interaction.comment.reported.v1' as const;
export const INTERACTION_POLL_CLOSED_V1 = 'interaction.poll.closed.v1' as const;

/**
 * Er is een reactie geplaatst, met de @-vermeldingen erin.
 *
 * **Zonder de tekst van de reactie**, net als bij een persoonlijk bericht: een event gaat door de
 * outbox en blijft in het eventspoor staan. `notifications` heeft de tekst niet nodig — een melding
 * zegt "Ana vermeldde je in een reactie" en niet wat ze schreef (ADR-0038).
 *
 * `mentions` is al gefilterd op de doelgroep van het bericht: wie het bericht niet mag zien, staat
 * hier niet in. Die filtering hoort bij `interaction`, dat `CommunicationApi.canView` kent;
 * `notifications` zou die vraag niet kunnen stellen.
 *
 * Het event vertrekt ook als `mentions` leeg is. Dat is met opzet: een auditspoor (Stap 18) en een
 * moderatiewachtrij willen weten dát er gereageerd is, niet alleen dat er iemand vermeld werd.
 */
export const commentCreatedV1Schema = eventSchema({
  commentId: z.guid(),
  postId: z.guid(),
  authorId: z.guid(),
  /** Alleen om de melding te kunnen maken: "Ana vermeldde je". Nooit de inhoud. */
  authorDisplayName: z.string(),
  /** Al gefilterd op wie het bericht mag zien; de auteur zelf staat er nooit in. */
  mentions: z.array(z.guid()),
  /** De schrijver van het bericht, voor "reactie op je bericht" (Stap 16). */
  postAuthorId: z.guid().nullable().optional(),
});
export type CommentCreatedV1 = z.infer<typeof commentCreatedV1Schema>;

/**
 * Iemand heeft een reactie gemeld.
 *
 * Zonder de tekst en **zonder wie het meldde**. Een melder die zichtbaar wordt, meldt de tweede
 * keer niet meer; dat is precies waarom het oude platform geen werkende meldknop had.
 */
export const commentReportedV1Schema = eventSchema({
  commentId: z.guid(),
  postId: z.guid(),
  /** Hoeveel meldingen deze reactie nu heeft; een moderator sorteert daarop. */
  reportCount: z.number().int(),
});
export type CommentReportedV1 = z.infer<typeof commentReportedV1Schema>;

/**
 * Een peiling is gesloten.
 *
 * Nog geen luisteraar in deze stap. Het event bestaat omdat "de uitslag is binnen" iets is waar een
 * melding bij hoort (`resultsVisibility: 'after_close'`), en dat is een voorkeur — Stap 16. Het nu
 * publiceren betekent dat die stap niets in `interaction` hoeft te wijzigen.
 */
export const pollClosedV1Schema = eventSchema({
  pollId: z.guid(),
  postId: z.guid(),
  voterCount: z.number().int(),
});
export type PollClosedV1 = z.infer<typeof pollClosedV1Schema>;

export const interactionEventCatalog = {
  [INTERACTION_COMMENT_CREATED_V1]: commentCreatedV1Schema,
  [INTERACTION_COMMENT_REPORTED_V1]: commentReportedV1Schema,
  [INTERACTION_POLL_CLOSED_V1]: pollClosedV1Schema,
} as const;
